import LoadingSpinner from '../LoadingSpinner'
import { Space } from '../../Hooks/useRecommendation/useSpaceRecommendations'
import { Status, Wrapper } from '@googlemaps/react-wrapper'
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES } from '../../Constants'
import { motion } from 'framer-motion'
import { ReactElement, useEffect, useRef, useState } from 'react'

const MAP_STYLE: google.maps.MapTypeStyle[] = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'administrative',
    elementType: 'labels',
    stylers: [{ visibility: 'simplified' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }],
  },
]

type GoogleMapProps = {
  spaces: Space[]
  initialLocation?: { lat: number; lon: number } | null
  onHover?: (space: Space | null) => void
  selectedSpaceIds?: number[]
  focusedSpaceId?: number | null
}

/* eslint-disable max-len */
const LOCATION_ON = `<svg xmlns="http://www.w3.org/2000/svg" height="48px" viewBox="0 -960 960 960" width="48px">
  <path d="M480-80Q319-217 239.5-334.5T160-552q0-150 96.5-239T480-880q127 0 223.5 89T800-552q0 100-79.5 217.5T480-80Z" fill="#AF231C"/>
  
  <path d="M480-388q54-50 84-80t47-50q16-20 22.5-37t6.5-37q0-36-26-62t-62-26q-21 0-40.5 8.5T480-648q-12-15-31-23.5t-41-8.5q-36 0-62 26t-26 62q0 21 6 37t22 36q17 20 46 50t86 81Z" fill="#FFFFFF"/>
</svg>`
/* eslint-enable max-len */

const createMarkerElement = (isSelected: boolean, isFocused: boolean): HTMLDivElement => {
  const el = document.createElement('div')
  el.style.cursor = 'pointer'
  if (isSelected) {
    el.style.width = '40px'
    el.style.height = '40px'
    el.style.display = 'flex'
    el.style.alignItems = 'center'
    el.style.justifyContent = 'center'
    el.innerHTML = LOCATION_ON
  } else if (isFocused) {
    el.style.width = '28px'
    el.style.height = '28px'
    el.style.borderRadius = '50%'
    el.style.backgroundColor = '#000000'
    el.style.border = '3px solid #ffffff'
    el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.4)'
  } else {
    el.style.width = '24px'
    el.style.height = '24px'
    el.style.borderRadius = '50%'
    el.style.backgroundColor = '#ffffff'
    el.style.border = '2px solid #AF231C'
    el.style.boxShadow = '0 1px 3px rgba(0,0,0,0.3)'
  }
  return el
}

const GoogleMap = ({
  spaces,
  initialLocation,
  onHover,
  selectedSpaceIds,
  focusedSpaceId,
}: GoogleMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInitialized = useRef(false)
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([])
  const activePopupRef = useRef<google.maps.OverlayView | null>(null)
  const prevSpacesRef = useRef<Space[]>([])
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)

  useEffect(() => {
    if (!mapRef.current || mapInitialized.current) return

    const initializeMap = () => {
      if (!window.google || !mapRef.current || mapInitialized.current) return

      const initialCenter = initialLocation
        ? { lat: initialLocation.lat, lng: initialLocation.lon }
        : { lat: 51.5074, lng: -0.1278 }

      const gMap = new window.google.maps.Map(mapRef.current, {
        center: initialCenter,
        zoom: initialLocation ? 12.5 : 10,
        mapId: 'DEMO_MAP_ID',
        styles: MAP_STYLE,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        zoomControl: true,
        disableDefaultUI: false,
        gestureHandling: 'greedy',
        draggable: true,
        scrollwheel: true,
        keyboardShortcuts: true,
        disableDoubleClickZoom: false,
      })

      setMap(gMap)
      setMapLoaded(true)
      mapInitialized.current = true
    }

    if (window.google) {
      initializeMap()
    } else {
      const checkGoogleMaps = setInterval(() => {
        if (window.google) {
          clearInterval(checkGoogleMaps)
          initializeMap()
        }
      }, 100)
      return () => clearInterval(checkGoogleMaps)
    }
  }, [initialLocation])

  useEffect(() => {
    if (!map) return

    markersRef.current.forEach((m) => {
      m.map = null
      m.content = null
    })
    markersRef.current = []
    if (activePopupRef.current) {
      activePopupRef.current.setMap(null)
      activePopupRef.current = null
    }

    const validSpaces = spaces.filter(
      (s) =>
        s?.location?.latlng &&
        typeof s.location.latlng.lat === 'number' &&
        typeof s.location.latlng.lng === 'number' &&
        !Number.isNaN(s.location.latlng.lat) &&
        !Number.isNaN(s.location.latlng.lng)
    )

    if (validSpaces.length === 0) return

    class HoverPopup extends google.maps.OverlayView {
      private position: google.maps.LatLngLiteral
      private containerDiv: HTMLDivElement
      constructor(position: google.maps.LatLngLiteral, label: string) {
        super()
        this.position = position
        const anchor = document.createElement('div')
        anchor.style.position = 'absolute'
        anchor.style.transform = 'translate(-50%, -140%)'
        anchor.style.pointerEvents = 'none'

        const bubble = document.createElement('div')
        bubble.style.background = '#ffffff'
        bubble.style.padding = '4px 8px'
        bubble.style.borderRadius = '4px'
        bubble.style.boxShadow = '0 2px 6px rgba(0,0,0,0.25)'
        bubble.style.fontSize = '14px'
        bubble.style.fontWeight = '600'
        bubble.style.color = '#111827'
        bubble.style.whiteSpace = 'nowrap'
        bubble.style.pointerEvents = 'none'
        bubble.textContent = label

        const arrow = document.createElement('div')
        arrow.style.position = 'absolute'
        arrow.style.left = '50%'
        arrow.style.bottom = '-5px'
        arrow.style.transform = 'translateX(-50%)'
        arrow.style.width = '0'
        arrow.style.height = '0'
        arrow.style.borderLeft = '5px solid transparent'
        arrow.style.borderRight = '5px solid transparent'
        arrow.style.borderTop = '5px solid #ffffff'

        const wrapper = document.createElement('div')
        wrapper.appendChild(bubble)
        wrapper.appendChild(arrow)
        anchor.appendChild(wrapper)

        this.containerDiv = anchor
        HoverPopup.preventMapHitsAndGesturesFrom(this.containerDiv)
      }
      onAdd(): void {
        this.getPanes()?.floatPane.appendChild(this.containerDiv)
      }
      onRemove(): void {
        this.containerDiv.parentElement?.removeChild(this.containerDiv)
      }
      draw(): void {
        const projection = this.getProjection()
        if (!projection) return
        const pos = projection.fromLatLngToDivPixel(
          new google.maps.LatLng(this.position.lat, this.position.lng)
        )
        if (!pos) return
        this.containerDiv.style.left = `${pos.x}px`
        this.containerDiv.style.top = `${pos.y}px`
        this.containerDiv.style.display =
          Math.abs(pos.x) < 6000 && Math.abs(pos.y) < 6000 ? 'block' : 'none'
      }
    }

    const showPopupFor = (space: Space) => {
      if (activePopupRef.current) {
        activePopupRef.current.setMap(null)
        activePopupRef.current = null
      }
      const popup = new HoverPopup(
        { lat: space.location.latlng.lat, lng: space.location.latlng.lng },
        space.venueName
      )
      onHover?.(space)
      popup.setMap(map!)
      activePopupRef.current = popup
    }

    const hidePopup = () => {
      if (activePopupRef.current) {
        onHover?.(null)
        activePopupRef.current.setMap(null)
        activePopupRef.current = null
      }
    }

    const addMarker = (space: Space, orderIndex: number) => {
      const isSelected = selectedSpaceIds?.includes(Number(space.id)) ?? false
      const isFocused = focusedSpaceId != null && Number(space.id) === focusedSpaceId
      const el = createMarkerElement(isSelected, isFocused)
      el.addEventListener('mouseover', () => showPopupFor(space))
      el.addEventListener('mouseleave', hidePopup)

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: space.location.latlng.lat, lng: space.location.latlng.lng },
        map,
        title: space.venueName,
        content: el,
        zIndex: isFocused ? 300 + orderIndex : isSelected ? 200 + orderIndex : 100 + orderIndex,
      })
      markersRef.current.push(marker)
    }

    validSpaces.forEach((s, i) => addMarker(s, i))

    const spacesChanged = prevSpacesRef.current !== spaces
    prevSpacesRef.current = spaces

    if (spacesChanged) {
      const top3 = validSpaces.slice(0, 3)
      const bounds = new google.maps.LatLngBounds()
      top3.forEach((s) => bounds.extend({ lat: s.location.latlng.lat, lng: s.location.latlng.lng }))
      map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 })
      google.maps.event.addListenerOnce(map, 'idle', () => {
        if ((map.getZoom() ?? 0) > 15) map.setZoom(15)
      })
    }
  }, [map, spaces, selectedSpaceIds, focusedSpaceId])

  useEffect(() => {
    if (!map || focusedSpaceId == null) return

    const target = spaces.find((s) => Number(s.id) === focusedSpaceId)
    const latlng = target?.location?.latlng
    if (
      !latlng ||
      typeof latlng.lat !== 'number' ||
      typeof latlng.lng !== 'number' ||
      Number.isNaN(latlng.lat) ||
      Number.isNaN(latlng.lng)
    ) {
      return
    }

    map.panTo({ lat: latlng.lat, lng: latlng.lng })
    if ((map.getZoom() ?? 0) < 14) map.setZoom(14)
  }, [map, focusedSpaceId, spaces])

  return (
    <motion.div
      className='h-full w-full rounded-lg overflow-hidden relative'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3 }}>
      {!mapLoaded && (
        <motion.div
          className='absolute inset-0 z-10 flex items-center justify-center bg-gray-50'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}>
          <motion.div
            className='w-8 h-8 border-2 border-gray-300 border-t-[#AF231C] rounded-full'
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
          />
        </motion.div>
      )}
      <div ref={mapRef} className='h-full w-full' />
    </motion.div>
  )
}

const GoogleMapElement = ({
  spaces,
  initialLocation,
  onHover,
  selectedSpaceIds,
  focusedSpaceId,
}: GoogleMapProps) => {
  const render = (status: Status): ReactElement => {
    switch (status) {
      case Status.LOADING:
        return <LoadingSpinner fadeIn={false} fullPage={false} />
      case Status.FAILURE:
        return (
          <div className='flex items-center justify-center h-full'>
            <p className='text-sm text-gray-500'>Google Maps failed to load.</p>
          </div>
        )
      case Status.SUCCESS:
        return (
          <GoogleMap
            initialLocation={initialLocation}
            spaces={spaces}
            onHover={onHover}
            selectedSpaceIds={selectedSpaceIds}
            focusedSpaceId={focusedSpaceId}
          />
        )
    }
  }

  return <Wrapper apiKey={GOOGLE_MAPS_API_KEY} libraries={GOOGLE_MAPS_LIBRARIES} render={render} />
}

export default GoogleMapElement
